import axios, { AxiosRequestConfig } from 'axios';
import { ApiConfig } from '../config/apiConfig';
import { GeneralConfig } from '../config/generalConfig';
import { isModuleLoaded } from '../stores/moduleStore';
import {
  buildUrl,
  isErrorCodeExists,
  parseApiErrorResponse,
  parseApiSuccessResponse,
  parseErrorString,
} from '../utils/apiUtils';
import { getAuthToken, getDemoPlan, getStoreAuthToken } from '../utils/storage';
import { NetworkException } from '../utils/apiException';
import saveAs from 'file-saver';
import { configureSystem } from '../utils/systemUtils';
import { useAppLoadedStore } from '../stores/appLoadedStore';
import { useAuthStore } from '../stores/authStore';
import { useNavigationStore } from '../stores/navigationStore';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../routes/paths';
import { useDemoInfoStore } from '../stores/demoInfoStore';

export class ApiService {
  get host(): string {
    return ApiConfig.host;
  }

  get protocol(): string {
    return ApiConfig.protocol;
  }

  get apiKey(): string {
    return ApiConfig.apiKey;
  }

  get pathPrefix(): string {
    return ApiConfig.pathPrefix;
  }

  get headers(): Record<string, string> {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      'Api-Key': this.apiKey,
      'Web-Request': '1',
      // 'ngrok-skip-browser-warning': 'true',
    };
  }

  get authHeader(): Record<string, string> {
    const authToken = getAuthToken();
    if (authToken) {
      return {
        Authorization: `Bearer ${authToken}`,
      };
    }
    return {};
  }

  async get(
    path: string,
    options: {
      queryParams?: Record<string, any>;
      authenticate?: boolean;
      prefix?: string;
      includeStoreToken?: boolean;
    } = {}
  ): Promise<any> {
    const {
      queryParams = {},
      authenticate = true,
      prefix,
      includeStoreToken = false,
    } = options;

    let fullPath = `${this.pathPrefix}${path}`;
    if (prefix) {
      fullPath = `${prefix}${path}`;
    }

    const storeAuthToken = getStoreAuthToken();

    const demoPlan = getDemoPlan();
    if (GeneralConfig.isDemo && demoPlan) {
      queryParams['demo_plan'] = demoPlan;
    }

    const hasModuleLoaded = isModuleLoaded();
    if (!hasModuleLoaded) {
      queryParams['load_modules'] = '1';
    }

    const url = buildUrl(this.protocol, this.host, fullPath, queryParams);
    const config: AxiosRequestConfig = {
      url,
      method: 'GET',
      headers: {
        ...this.headers,
        ...(authenticate || !isModuleLoaded ? this.authHeader : {}),
        ...(includeStoreToken && storeAuthToken
          ? { 'Store-Token': storeAuthToken }
          : {}),
      },
    };

    const { status, setAppLoaded } = useAppLoadedStore.getState();
    if (!status) {
      setAppLoaded();
    }

    try {
      const response = await axios(config);
      const responseJson = parseApiSuccessResponse(response);

      if (!hasModuleLoaded) {
        configureSystem(responseJson);
      }

      return responseJson;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          if (!hasModuleLoaded && error.response.data) {
            configureSystem(error.response.data);
          }
          const { unauthenticate, blockUser } = useAuthStore.getState();
          const { setCurrentMenu } = useNavigationStore.getState();
          if (error.response.status === 401) {
            unauthenticate();
            setCurrentMenu('home');
          }
          if (error.response.status === 500) {
            if (
              GeneralConfig.isDemo &&
              isErrorCodeExists(
                parseErrorString(error.response.data),
                'demo_blocked'
              )
            ) {
              const { blockDemo } = useDemoInfoStore.getState();
              blockDemo();
            }
            if (
              isErrorCodeExists(
                parseErrorString(error.response.data),
                'user_blocked'
              )
            ) {
              blockUser();
            }
          }
        }
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }

  async post(
    path: string,
    data?: Record<string, any>,
    options: {
      authenticate?: boolean;
      includeStoreToken?: boolean;
    } = {}
  ): Promise<any> {
    const { authenticate = true, includeStoreToken = false } = options;

    const storeAuthToken = getStoreAuthToken();
    const demoPlan = getDemoPlan();
    const queryParams: Record<string, any> = {};

    if (GeneralConfig.isDemo && demoPlan) {
      queryParams['demo_plan'] = demoPlan;
    }

    const url = buildUrl(this.protocol, this.host, path, queryParams);
    const config: AxiosRequestConfig = {
      url,
      method: 'POST',
      headers: {
        ...this.headers,
        ...(authenticate ? this.authHeader : {}),
        ...(includeStoreToken && storeAuthToken
          ? { 'Store-Token': storeAuthToken }
          : {}),
      },
      data,
    };

    try {
      const response = await axios(config);
      const responseJson = parseApiSuccessResponse(response);
      return responseJson;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }

  async postMultipart(
    path: string,
    fileName: string,
    files: File[],
    data?: Record<string, any>,
    options: {
      authenticate?: boolean;
      includeStoreToken?: boolean;
    } = {}
  ): Promise<any> {
    const { authenticate = true, includeStoreToken = false } = options;

    const storeAuthToken = getStoreAuthToken();
    const demoPlan = getDemoPlan();
    const queryParams: Record<string, any> = {};

    if (GeneralConfig.isDemo && demoPlan) {
      queryParams['demo_plan'] = demoPlan;
    }

    const url = buildUrl(this.protocol, this.host, path, queryParams);
    const formData = new FormData();

    if (data) {
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
    }

    files.forEach((file) => {
      formData.append(fileName, file);
    });

    const config: AxiosRequestConfig = {
      url,
      method: 'POST',
      headers: {
        ...this.headers,
        ...(authenticate ? this.authHeader : {}),
        ...(includeStoreToken && storeAuthToken
          ? { 'Store-Token': storeAuthToken }
          : {}),
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    };

    try {
      const response = await axios(config);
      const responseJson = parseApiSuccessResponse(response);
      return responseJson;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }

  async put(
    path: string,
    data?: Record<string, any>,
    options: {
      authenticate?: boolean;
      includeStoreToken?: boolean;
    } = {}
  ): Promise<any> {
    const { authenticate = true, includeStoreToken = false } = options;

    const storeAuthToken = getStoreAuthToken();
    const demoPlan = getDemoPlan();
    const queryParams: Record<string, any> = {};

    if (GeneralConfig.isDemo && demoPlan) {
      queryParams['demo_plan'] = demoPlan;
    }

    const url = buildUrl(this.protocol, this.host, path, queryParams);
    const config: AxiosRequestConfig = {
      url,
      method: 'PUT',
      headers: {
        ...this.headers,
        ...(authenticate ? this.authHeader : {}),
        ...(includeStoreToken && storeAuthToken
          ? { 'Store-Token': storeAuthToken }
          : {}),
      },
      data,
    };

    try {
      const response = await axios(config);
      const responseJson = parseApiSuccessResponse(response);
      return responseJson;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }

  async delete(
    path: string,
    data?: Record<string, any>,
    options: {
      authenticate?: boolean;
      includeStoreToken?: boolean;
    } = {}
  ): Promise<any> {
    const { authenticate = true, includeStoreToken = false } = options;

    const storeAuthToken = getStoreAuthToken();
    const demoPlan = getDemoPlan();
    const queryParams: Record<string, any> = {};

    if (GeneralConfig.isDemo && demoPlan) {
      queryParams['demo_plan'] = demoPlan;
    }

    const url = buildUrl(this.protocol, this.host, path, queryParams);
    const config: AxiosRequestConfig = {
      url,
      method: 'DELETE',
      headers: {
        ...this.headers,
        ...(authenticate ? this.authHeader : {}),
        ...(includeStoreToken && storeAuthToken
          ? { 'Store-Token': storeAuthToken }
          : {}),
      },
      data,
    };

    try {
      const response = await axios(config);
      const responseJson = parseApiSuccessResponse(response);
      return responseJson;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }

  async downloadFile(
    path: string,
    filename?: string,
    isSecure = false
  ): Promise<void> {
    const url = buildUrl(this.protocol, this.host, path);
    const config: AxiosRequestConfig = {
      url,
      method: 'GET',
      responseType: 'blob',
      headers: {
        ...this.headers,
        ...(isSecure ? this.authHeader : {}),
      },
    };

    try {
      const response = await axios(config);
      if (response.status === 200) {
        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        saveAs(blob, filename || path.split('/').pop()!);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        parseApiErrorResponse(error);
      } else {
        throw new NetworkException();
      }
    }
  }
}
